import { IOfferState, IUserState } from '../../../redux/reducers';
import { IPublishTermsSuccessPayload } from '../../../redux/types/publishTermsPayload';

export type TMicrofrontendsAPI = {
  buyPackage: (parameters: IBuyPackageParams) => Promise<unknown>;
  setOffer: (parameters: IOfferState) => Promise<unknown>;
  setUser: (parameters: IUserState) => Promise<unknown>;
  loadPublishTerms: () => Promise<unknown>;
  getServices: () => Promise<unknown>;
  checkPaymentStatus: (paymentToken: string, type?: EPaymentCheckType) => Promise<unknown>;
};

export interface IBuyPackageParams {
  packageData: IBuyPackageData;
  billingUserType: TBillingUserType;
  paymentInitSource: EPaymentInitSource;
}

type TBillingUserType = 'natural' | 'legal';

export interface IBuyPackageData {
  packageId?: string | number;
  type?: EPackageType;
  services?: IBuyPackageService[];
  price?: number;
  marketingPrice?: number | null;
  auctionPoints?: number | null;
  cashbackPercent?: number | null;
  discountPercent?: number | null;
  categoriesView?: string;
  regionNames?: string[];
  activationDate?: string;
  endDate?: string;
  durationInDays?: number;
  autoProlong?: boolean;
  autoProlongAvailable?: boolean;
  polygonIds?: number[];
  internalType?: EInternalPackageType;
  gifts?: IBuyPackageGift[];
  totalAuctionPoints?: number;
  isPromotionByAuction?: boolean;
  confirmationToken?: string;
  balanceDetails?: IBalanceDetails;
  level?: ELevel;
  confirmationPopupTitle?: string;
  widgetSource?: EBuyPackageSource;
}

interface IBalanceDetails {
  balance: number;
  notEnough: number;
}

export enum EPackageType {
  /** Общее предложение (Быстрая сделка, Vip-пакет) */
  CommonOffer = 'commonOffer',
  /** Специальное предложение - сгенерировано на основе промокода */
  SpecialOffer = 'specialOffer',
  /** Персональное предложение - будет генерироваться на отдельных условиях */
  PersonalOffer = 'personalOffer',
  /** Общее предложение - подписки на публикации */
  CommonSubscription = 'commonSubscription',
  /** Смарт-пакет */
  SmartPackage = 'smartPackage',
  /** Пакет "Всё включено" */
  AllIn = 'allIn',
  /** Подписка */
  Subscription = 'subscription',
}

export interface IBuyPackageService {
  icon: string;
  name: string;
  marketingPrice: number;
  count: number;
  serviceTypes: EServiceTypes[];
  serviceDurationInDays?: number;
  auctionPoints: number;
}

/** Типы пакетов для внутреннего использования на фронте */
export enum EInternalPackageType {
  /** Пакет (старый раздел, вкладка "Купить пакет") */
  LegacyPackage = 'legacy_package',
  /** Подписка (новый раздел, вкладка "Купить пакет") */
  Subscription = 'subscription',
  /** Персональный пакет (новый раздел, вкладка "Персональные предложения") */
  PersonalPackage = 'personal_package',
  /** Персональная подписка (новый раздел, вкладка "Персональные предложения") */
  PersonalSubscription = 'personal_subscription',
  /** Смарт-пакет (новый раздел, вкладка "Персональные предложения") */
  SmartPackage = 'smart_package',
  /** Конструктор (новый и старый разделы, вкладка "Собрать свой пакет") */
  CustomPackage = 'custom_package',
}

export interface IBuyPackageGift {
  icon: string;
  name: string;
  count: number;
  durationInDays: number;
}

export enum ELevel {
  /** Бронзовый **/
  Bronze = 'bronze',
  /** Серебряный **/
  Silver = 'silver',
  /** Золотой **/
  Gold = 'gold',
  /** Платиновый **/
  Platinum = 'platinum',
}

export enum EBuyPackageStatus {
  Confirm = 'Confirm',
  Ok = 'Ok',
  OkWithDeposit = 'OkWithDeposit',
  NotEnoughMoney = 'NotEnoughMoney',
  ConfirmUseBalance = 'ConfirmUseBalance',
  PayOnlineWidget = 'PayOnlineWidget',
  TariffNotAvailable = 'TariffNotAvailable',
  Error = 'Error',
}

export enum EServiceTypes {
  /** Платная публикация **/
  DebitObject = 'DebitObject',
  /** Премиум публикация **/
  PremiumObject = 'PremiumObject',
  /** Тор 3 **/
  Top3 = 'Top3',
  /** Выделение цветом **/
  Highlight = 'Highlight',
  /** Подмена номера, Calltracking, оно же "защита номера" **/
  Calltracking = 'calltracking',
  /** Бесплатная публикация **/
  FreeObject = 'FreeObject',
  /** Выгрузка объявлений через XML **/
  XmlImport = 'XmlImport',
  /** Абонентская плата за пакет **/
  SubscriptionForPackage = 'SubscriptionForPackage',
  /** Признак статуса ПРО **/
  StatusPro = 'StatusPro',
  /**  **/
  ServicePackageActivation = 'ServicePackageActivation',
  /** Штрафное списание **/
  Penalty = 'Penalty',
  /** Отмена платежа **/
  OrderCancellation = 'OrderCancellation',
  /** перенос платежа **/
  OrderTransfer = 'OrderTransfer',
  /** Произвольное списание **/
  TechSpend = 'TechSpend',
  /** Перенос произвольной суммы **/
  TechTransfer = 'TechTransfer',
  /** Списание истекшего бонусного пополнения **/
  BonusPaymentExpiration = 'BonusPaymentExpiration',
  /** Списание за аукцион **/
  Auction = 'auction',
  /** Списание за заявку на спрос **/
  Demand = 'demand',
  /** Списание за пакет заявок на спрос **/
  DemandPackage = 'demandPackage',
  /** Коллтрекинг по модели CPL **/
  CplCalltracking = 'cplCalltracking',
  /** Динамическая услуга **/
  Dynamic = 'dynamic',
}

export const enum EPaymentInitSource {
  /** Пакет */
  LkRealtorPackage = 'packageLkRealtor',
  /** Конструктор  */
  LkRealtorCustomPackage = 'customPackageLkRealtor',
  /** Подписка  */
  LkRealtorSubscription = 'subscriptionLkRealtor',
  /** Персональный пакет */
  LkRealtorPersonalPackage = 'personalPackageLkRealtor',
  /** Персональная подписка  */
  LkRealtorPersonalSubscription = 'personalSubscriptionLkRealtor',
  /** Смарт-пакет  */
  LkRealtorSmartPackage = 'smartPackageLkRealtor',
  /** Пополнение в сайд-баре лк соба  */
  LkOwnerDeposit = 'depositLkHomeowner',
  /** Пополнение в лк риелтора  */
  LkRealtorDeposit = 'depositLkRealtor',
  /** платёж для идентификации по карте в лк соба */
  LkOwnerIdentification = 'lkHomeownerIdentification',
  /** платёж для идентификации по карте в лк риелтора */
  LkRealtorIdentification = 'identificationLkRealtor',
  /** платёж для идентификации по карте на ФП */
  AddFormIdentification = 'publishObjectIdentification',
  /** пополнение на форме подачи */
  AddFormDeposit = 'publishObject',
}

export enum EBuyPackageSource {
  LK = 'lk',
  AddForm = 'addForm',
  ServicePackageAdmin = 'servicePackageAdmin',
  SubscriptionAdmin = 'subscriptionAdmin',
  Mobile = 'mobile',
  Promocodes = 'promocodes',
  SmartPack = 'smartpack',
  AllIn = 'allIn',
  AddFormAllIn = 'addFormAllIn',
  AddFormSmartPack = 'addFormSmartPack',
}

export enum EPaymentCheckType {
  Balance = 'Balance',
  Package = 'Package',
}

export type TLoadPublishTermsResult = ILoadPublishTermsSuccess | ILoadPublishTermsFail;

export interface ILoadPublishTermsSuccess {
  success: true;
  payload: IPublishTermsSuccessPayload;
}

export interface ILoadPublishTermsFail {
  success: false;
  payload: string;
}
